import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Images, CreditCard, TrendingUp } from "lucide-react";
import { dashboardAPI } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import UserLayout from "../../components/UserLayout";

const AdminDashboard = () => {
    const { t } = useLanguage();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await dashboardAPI.getStats();
                setStats(response.data);
            } catch (error) {
                console.error("Error fetching stats:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    const statCards = [
        {
            label: t('total_users'),
            value: stats?.total_users || 0,
            icon: Users,
            color: "text-blue-600",
            bg: "bg-blue-50"
        },
        {
            label: t('total_images'),
            value: stats?.total_images || 0,
            icon: Images,
            color: "text-green-600",
            bg: "bg-green-50"
        },
        {
            label: t('total_transactions'),
            value: stats?.total_transactions || 0,
            icon: CreditCard,
            color: "text-yellow-600",
            bg: "bg-yellow-50"
        }
    ];

    return (
        <UserLayout>
            <div className="space-y-8" data-testid="admin-dashboard">
                <div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{t('admin_dashboard')}</h1>
                    <p className="text-gray-600 mt-1">{t('admin_subtitle')}</p>
                </div>

                {/* Stats */}
                <div className="grid md:grid-cols-3 gap-6">
                    {statCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`w-14 h-14 rounded-2xl ${stat.bg} flex items-center justify-center`}>
                                        <Icon className={`w-7 h-7 ${stat.color}`} />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">{stat.label}</p>
                                        <p className="text-3xl font-bold text-gray-900">
                                            {loading ? "-" : stat.value}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Quick Links */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { label: t('manage_packages'), href: "/admin/packages", color: "from-red-500 to-orange-500" },
                        { label: t('manage_users'), href: "/admin/users", color: "from-blue-500 to-cyan-500" },
                        { label: t('manage_backgrounds'), href: "/admin/backgrounds", color: "from-green-500 to-emerald-500" },
                        { label: t('settings_title'), href: "/admin/settings", color: "from-purple-500 to-pink-500" }
                    ].map((link, index) => (
                        <a
                            key={index}
                            href={link.href}
                            className={`block p-6 rounded-2xl bg-gradient-to-r ${link.color} text-white hover:shadow-lg transition-all duration-200`}
                        >
                            <h3 className="font-heading text-lg font-semibold">{link.label}</h3>
                        </a>
                    ))}
                </div>
            </div>
        </UserLayout>
    );
};

export default AdminDashboard;
